import authService from './authService';
import { API_BASE_URL } from '../config/api';
import { authFetch } from './session';

const API_URL = API_BASE_URL;

/**
 * Every call carries the login token. There is deliberately no way to skip
 * it: the onboarding's ``skipAuth`` call (01.10.2026) went to an endpoint that
 * picked the salon by e-mail — anyone could act for any salon.
 */
type ApiOptions = RequestInit;

/**
 * Non-2xx response. Carries the HTTP status so callers can react to specific
 * codes (409 reply window closed, 429 rate limit, …) instead of parsing text.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

class ApiService {
  private static instance: ApiService;
  
  private constructor() {}
  
  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }
  
  private async extractErrorMessage(response: Response): Promise<string> {
    try {
      const data = await response.clone().json();
      if (data && typeof data === 'object') {
        if (typeof (data as any).detail === 'string') return (data as any).detail;
        // FastAPI-Validierung (422): detail ist eine Liste {loc, msg, type}.
        // Ohne das sah der Nutzer nur „HTTP error! Status: 422".
        if (Array.isArray((data as any).detail)) {
          const messages = (data as any).detail
            .map((item: any) => (typeof item?.msg === 'string' ? item.msg : null))
            .filter(Boolean);
          if (messages.length) return `Eingabe ungültig: ${messages.join(' · ')}`;
        }
        if (typeof (data as any).error === 'string') return (data as any).error;
        if (typeof (data as any).message === 'string') return (data as any).message;
      }
    } catch {
      // Body wasn't JSON (or was empty) — fall through to the generic message.
    }
    return `HTTP error! Status: ${response.status}`;
  }

  public async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    try {
      const headers = new Headers(options.headers);
      headers.set('Content-Type', 'application/json');

      const response = await authFetch(`${API_URL}${endpoint}`, { ...options, headers });

      if (response.status === 401) {
        // Final: authFetch already tried to renew the session and told the app.
        throw new Error('Authentication expired');
      }
      
      if (!response.ok) {
        throw new ApiError(await this.extractErrorMessage(response), response.status);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }
  
  public async get<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }
  
  public async post<T>(endpoint: string, data: any, options: ApiOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
  
  public async put<T>(endpoint: string, data: any, options: ApiOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }
  
  public async patch<T>(endpoint: string, data: any, options: ApiOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  public async delete<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const apiService = ApiService.getInstance();
export default apiService; 